export type Message<T = any> = {
    meta: string;
    data: T;
};

export type Listener<T = any> = (message: Message<T>) => void;

export type Events = 'connect' | 'disconnect';

export type Event = {
    callback: () => void;
};

const RECONNECT_TIMEOUT = 10 * 1000; // timeout (ms) before try to connect again
const PING_INTERVAL = 20 * 1000; // how often (ms) to send "ping"
const KEEP_ALIVE_INTERVAL = 5 * 1000; // how often (ms) check if the connection is open
const DISCONNECT_RETRY_TIMEOUT = 1000; // how long (ms) to wait to reconnect after closed connection?
const DISCONNECT_RETRY_THRESHOLD = 2; // if retry fails to connect (i.e. no internet), how much to increase the retry timeout for each new request (2 = +100%, so the first try is 1 second, second try is 2 seconds, third try is 4 seconds and so on)
const DISCONNECT_RETRY_MAX_TIMEOUT = 60 * 1000; // max timeout (ms) to hit by the threshold, after this time the connection is considered closed

export default class WS {
    private url: string;
    private disconnectTries = 0;
    private connecting = false;
    private listeners: Record<string, Listener> = {};
    private events: Record<Events, Event[]> = {
        connect: [],
        disconnect: [],
    };
    private timeouts: Record<string, number|null> = {
        ping: null,
        keepAlive: null,
        retry: null,
        reconnect: null,
    };

    public socket?: WebSocket;
    public isConnected = false;

    constructor(sessionId: string) {
        const url = document.getElementById('chat-app')!.getAttribute('data-url') || '';

        this.url = url.replace('http://', 'ws://').replace('https://', 'wss://') + '/ws/' + sessionId;
    }

    /**
     * Connect to the websocket
     * */
    connect() {
        if(this.connecting) return;

        this.connecting = true;

        this.clearAllTimeouts();

        this.isConnected = false;
        const ws = new WebSocket(this.url);

        this.timeouts.reconnect = makeTimeout(() => {
            this.connecting = false;
            this.connect();
        }, RECONNECT_TIMEOUT);

        // On connection established
        ws.onopen = () => {
            if(this.timeouts.reconnect) {
                clearTimeout(this.timeouts.reconnect);
                this.timeouts.reconnect = null;
            }

            this.socket = ws;
            this.disconnectTries = 0;
            this.connecting = false;
            this.isConnected = true;
            this.setPingPong();
            this.setKeepAlive();

            this.events.connect.forEach((listener) => {
                listener.callback.call(listener.callback);
            });
        };

        // On connection closed
        ws.onclose = () => {
            if(this.timeouts.reconnect) {
                clearTimeout(this.timeouts.reconnect);
                this.timeouts.reconnect = null;
            }

            this.connecting = false;

            if(ws !== this.socket && this.socket) return;

            const nextTimeout = Math.min(
                DISCONNECT_RETRY_TIMEOUT + (DISCONNECT_RETRY_TIMEOUT * DISCONNECT_RETRY_THRESHOLD * this.disconnectTries),
                DISCONNECT_RETRY_MAX_TIMEOUT,
            );

            this.timeouts.retry = makeTimeout(() => this.connect(), nextTimeout);

            this.disconnectTries++;
            this.isConnected = false;

            this.events.disconnect.forEach((listener) => {
                listener.callback.call(listener.callback);
            });
        };

        // On message received
        ws.onmessage = (e) => {
            try {
                const msg: Message = JSON.parse(e.data);

                if(msg.meta === 'connection') {
                    return;
                }

                Object.values(this.listeners).forEach((listener) => {
                    listener.call(listener, msg);
                });
            } catch(_) {
            }
        };
    }

    /**
     * Disconnect from the websocket
     * */
    disconnect() {
        this.clearAllTimeouts();

        if(!this.socket) return;

        this.socket.close();
    }

    /**
     * Force reconnect — resets retry counter and connects immediately
     * */
    reconnect() {
        this.clearAllTimeouts();
        this.disconnectTries = 0;
        this.connecting = false;

        if(this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
            this.socket.close();
        } else {
            this.connect();
        }
    }

    /**
     * Set ping-pong to keep connection alive
     * */
    setPingPong() {
        this.timeouts.ping = makeTimeout(() => {
            if(this.socket?.readyState === WebSocket.OPEN) {
                this.sendMessage('connection', 'ping');

                this.setPingPong();
            }
        }, PING_INTERVAL);
    }

    /**
     * Check if the connection is closed and reconnect if it is
     * */
    setKeepAlive() {
        this.timeouts.keepAlive = makeTimeout(() => {
            if(!this.socket || this.socket.readyState === WebSocket.CLOSING || this.socket.readyState === WebSocket.CLOSED) {
                this.connect();
            } else {
                this.setKeepAlive();
            }
        }, KEEP_ALIVE_INTERVAL);
    }

    /**
     * Listen for messages
     * */
    listen<T = any>(id: string, listener: Listener<T>) {
        this.listeners[id] = listener;
    }

    /**
     * Unbind listener
     * */
    unbind(id: string) {
        delete this.listeners[id];
    }

    /**
     * Send message
     * */
    sendMessage(meta: string, data: any) {
        if(!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

        const msg: Message = {
            meta,
            data: typeof data === 'string' || data === null ? data : JSON.stringify(data),
        };

        this.socket.send(JSON.stringify(msg));
    }

    /**
     * On events
     * */
    on(event: Events, callback: Event['callback']) {
        this.events[event].push({ callback });
    }

    /**
     * Off events
     * If callback is provided, removes only that specific event callback
     * If no callback is provided, clears all callbacks for the specified event
     * */
    off(event: Events, callback?: Event['callback']) {
        if(callback) {
            this.events[event] = this.events[event].filter((listener) => listener.callback !== callback);
        } else {
            this.events[event] = [];
        }
    }

    private clearAllTimeouts() {
        Object.keys(this.timeouts).forEach((key) => {
            if(this.timeouts[key]) {
                clearTimeout(this.timeouts[key]!);
                this.timeouts[key] = null;
            }
        });
    }
}

/**
 * When a browser tab is inactive, browser tends to throttle javascript (including timeouts), so this function will
 * create a setTimeout but with twice as shorter interval when the tab is inactive, making it more convenient
 * */
const makeTimeout = (callback: () => any, timeout: number) => {
    const n = setTimeout(callback, document.visibilityState === 'hidden' ? timeout / 2 : timeout);

    return n as any;
};
